export class CascadeModelItem {
  public val?: string | null;
  public vn?: string;
  public ch?: Map<string, CascadeModelItem[]> = new Map();

  /**
   * Function to create a root-level Map from JSON
   */
  public static createMapFromJson(jsonString: string): Map<string, CascadeModelItem> {

    const jsonData = JSON.parse(jsonString);
    const rootMap = new Map<string, CascadeModelItem>();

    jsonData.forEach((item: any) => {
      const cascadeItem = CascadeModelItem.createItemFromJson(item);
      if (cascadeItem.val) {
        rootMap.set(cascadeItem.val, cascadeItem);
      }
    });

    return rootMap;
  }

  /**
   * Create a three-level optimized lookup nested Map structure from JSON string
   * 
   * First level key: variable name (vn) "WEB_Region"
   * Second level key: variable value (val) "Australia"
   * Third level key: child variable name (vn) "DATAQUALIFIER"
   * Value: array of child variable values (val) ["Benchmark", "Non-Benchmark"]
   * 
   * @param jsonString
   * @returns
   */
  public static vNameValueVNameLookupCreate(jsonString: string): Map<string, Map<string, Map<string, string[]>>> {
    const collection = new Map<string, Map<string, Map<string, string[]>>>();

    const jsonData = JSON.parse(jsonString);
    const cascadeItems = this.convertJsonToCascadeItems(jsonData);

    // Build the collection from CascadeModelItems
    for (let i = 0; i < cascadeItems.length; i++) {
      const item = cascadeItems[i];
      this.addCascadeItemToCollection(item, collection);
    }

    return collection;
  }

  /**
   * Helper method to access array values from the collection
   */
  public static vNameValueVNameGetValues(
    collection: Map<string, Map<string, Map<string, string[]>>>,
    firstKeyVarName: string,   // e.g., varname "WEB_Region" 
    secondKeyValue: string,  // e.g., value "Australia"
    thirdKeyVarName: string | null = null  // e.g., varname "DATAQUALIFIER"
  ): string[] {

    const firstLevelMap = collection.get(firstKeyVarName);
    if (!firstLevelMap || firstLevelMap.size === 0) {
      return [];
    }

    const secondLevelMap = firstLevelMap.get(secondKeyValue);
    if (!secondLevelMap || secondLevelMap.size === 0) {
      return [];
    }

    if (!thirdKeyVarName) {

      const firstThirdKey = secondLevelMap.keys().next().value;
      return secondLevelMap.get(firstThirdKey) ?? [];

    }
    else
      return secondLevelMap.get(thirdKeyVarName) ?? [];
  }

  /**
   * Get the first key from the third level map i.e. the destination varName
   * that should be updated with the array of values as its datasource
   * @param collection
   * @param firstKeyVarName
   * @param secondKeyValue
   * @returns
   */
  public static vNameValueVNameGetLastKey(
    collection: Map<string, Map<string, Map<string, string[]>>>
    , firstKeyVarName: string   // e.g., varname "WEB_Region" 
    , secondKeyValue: string  // e.g., value "Australia"
  ): string | null {

    const firstLevelMap = collection.get(firstKeyVarName);
    if (!firstLevelMap || firstLevelMap.size === 0) {
      return null;
    }

    const secondLevelMap = firstLevelMap.get(secondKeyValue);
    if (!secondLevelMap || secondLevelMap.size === 0) {
      return null;
    }

    const firstThirdKey = secondLevelMap.keys().next().value;

    return firstThirdKey ?? null;
  }

  //---------------------- internal helpers -----------------------------

  /**
   * Function to recursively create a `CascadeModelItem` from a JSON object 
   */
  protected static createItemFromJson(jsonItem: any): CascadeModelItem {

    if (!jsonItem) {
      return new CascadeModelItem();
    }

    if (typeof jsonItem !== 'object') {
      throw new Error('Invalid JSON object');
    }

    if (Array.isArray(jsonItem)) {
      throw new Error('JSON object should not be an array');
    }

    const root = new CascadeModelItem();
    root.val = jsonItem.val || null;
    root.vn = jsonItem.vn || null;

    if (jsonItem.ch && Array.isArray(jsonItem.ch)) {

      root.ch = new Map();

      jsonItem.ch.forEach((child: any) => {
        const childItem = CascadeModelItem.createItemFromJson(child);

        // Use vn as the key for grouping children
        const key = childItem.vn || childItem.val || 'unknown';

        // If this key doesn't exist yet, initialize with array
        if (!root.ch!.has(key)) {
          root.ch!.set(key, []);
        }

        // Add this child to the array for this key
        root.ch!.get(key)!.push(childItem);
      });
    }

    return root;
  }

  protected static convertJsonToCascadeItems(jsonArray: any[]): CascadeModelItem[] {
    const items: CascadeModelItem[] = [];
    for (let i = 0; i < jsonArray.length; i++) {
      const jsonNode = jsonArray[i];
      const item = new CascadeModelItem();
      item.val = jsonNode.val;
      item.vn = jsonNode.vn;

      // Convert children array to Map grouped by vn
      if (jsonNode.ch && Array.isArray(jsonNode.ch)) {
        item.ch = new Map();
        for (let j = 0; j < jsonNode.ch.length; j++) {
          const childJson = jsonNode.ch[j];
          const key = childJson.vn || `child_${j}`;

          // If this vn key doesn't exist yet, initialize with array
          if (!item.ch.has(key)) {
            item.ch.set(key, []);
          }

          // Add this child to the array for this vn
          const childItem = new CascadeModelItem();
          childItem.val = childJson.val;
          childItem.vn = childJson.vn;
          item.ch.get(key)!.push(childItem);
        }
      }
      items.push(item);
    }
    return items;
  }

  protected static addCascadeItemToCollection(
    item: CascadeModelItem,
    collection: Map<string, Map<string, Map<string, string[]>>>
  ): void {
    const firstKey = item.vn;   // e.g., "WEB_Region"
    const secondKey = item.val; // e.g., "Australia"

    if (!firstKey || !secondKey) {
      return; // Skip items without vn or val
    }

    // Get or create the first level map
    if (!collection.has(firstKey)) {
      collection.set(firstKey, new Map());
    }
    const firstLevelMap = collection.get(firstKey)!;

    // Create the third level map from children
    const thirdLevelMap = this.createThirdLevelMapFromCascadeChildren(item.ch);

    // Add to second level
    firstLevelMap.set(secondKey, thirdLevelMap);
  }

  protected static createThirdLevelMapFromCascadeChildren(
    childrenMap?: Map<string, CascadeModelItem[]>
  ): Map<string, string[]> {
    const thirdLevelMap = new Map<string, string[]>();

    if (!childrenMap) {
      return thirdLevelMap;
    }

    // Iterate through the children map
    const childKeys = Array.from(childrenMap.keys());
    for (let i = 0; i < childKeys.length; i++) {
      const key = childKeys[i];
      const childItems = childrenMap.get(key);

      if (!childItems || childItems.length === 0) {
        continue;
      }

      // The key IS already the vn (e.g., "MF_Percentage")
      const thirdKey = key;

      // Extract all val values from the array
      const valueArray: string[] = [];
      for (let j = 0; j < childItems.length; j++) {
        const childValue = childItems[j].val;
        if (childValue) {
          valueArray.push(childValue);
        }
      }

      if (valueArray.length > 0) {
        thirdLevelMap.set(thirdKey, valueArray);
      }
    }

    return thirdLevelMap;
  }
}
